<template>
  <div>
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>商品管理</span>
        <div class="header-actions">
          <el-button :loading="loading" @click="loadData">刷新</el-button>
          <el-button type="primary" @click="goCreate">新增商品</el-button>
        </div>
      </div>

      <el-form :inline="true" :model="query" class="query-form">
        <el-form-item>
          <el-input v-model.trim="query.name" placeholder="商品名称" clearable />
        </el-form-item>
        <el-form-item>
          <el-select v-model="query.categoryId" placeholder="分类" clearable>
            <el-option v-for="item in categories" :key="item.id" :label="item.name" :value="item.id" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button @click="resetQuery">重置</el-button>
          <el-button type="primary" @click="loadData">查询</el-button>
        </el-form-item>
      </el-form>

      <el-alert
        v-if="loadError"
        class="state-block"
        title="商品数据加载失败"
        type="error"
        :closable="false"
        show-icon
      >
        <template slot>
          <span>可以稍后重试，或先检查后端服务是否正常。</span>
          <el-button type="text" @click="loadData">重新加载</el-button>
        </template>
      </el-alert>

      <template v-else>
        <el-empty v-if="!loading && !list.length" description="没有匹配的商品数据">
          <el-button type="primary" @click="goCreate">去新增商品</el-button>
        </el-empty>

        <template v-else>
          <el-table :data="list" border v-loading="loading">
            <el-table-column label="商品" min-width="320">
              <template slot-scope="{ row }">
                <div class="product-cell">
                  <el-image class="product-cell__image" :src="row.mainImage" fit="cover">
                    <div slot="error" class="product-cell__placeholder">无图</div>
                  </el-image>
                  <div class="product-cell__content">
                    <div class="product-cell__title-row">
                      <div class="product-cell__title">{{ row.name }}</div>
                      <el-tag v-if="row.groupBuyEnabled" size="mini" type="warning">团购中</el-tag>
                    </div>
                    <div class="product-cell__meta">{{ row.subTitle || '暂无副标题' }}</div>
                    <div v-if="row.groupBuyEnabled" class="product-cell__group-buy">
                      团购价 ¥{{ formatPrice(row.groupBuyPrice) }} · {{ row.groupBuyPeopleCount }}人成团
                    </div>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="分类" width="120">
              <template slot-scope="{ row }">
                {{ categoryName(row.categoryId) }}
              </template>
            </el-table-column>
            <el-table-column label="价格区间" width="180">
              <template slot-scope="{ row }">
                ¥{{ formatPrice(row.minPrice) }} - ¥{{ formatPrice(row.maxPrice) }}
              </template>
            </el-table-column>
            <el-table-column prop="totalStock" label="库存" width="100" />
            <el-table-column prop="salesCount" label="销量" width="100" />
            <el-table-column label="状态" width="120">
              <template slot-scope="{ row }">
                <el-tag :type="row.status ? 'success' : 'info'">{{ row.status ? '上架' : '下架' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="220">
              <template slot-scope="{ row }">
                <el-button type="text" @click="goEdit(row.id)">编辑</el-button>
                <el-button type="text" @click="toggleStatus(row)">{{ row.status ? '下架' : '上架' }}</el-button>
                <el-button type="text" @click="removeRow(row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="table-footer">
            <div class="table-footer__meta">共 {{ total }} 条</div>
            <el-pagination
              background
              layout="prev, pager, next, sizes"
              :current-page="query.pageNum"
              :page-sizes="[10, 20, 50]"
              :page-size="query.pageSize"
              :total="total"
              @current-change="changePage"
              @size-change="changePageSize"
            />
          </div>
        </template>
      </template>
    </el-card>
  </div>
</template>

<script>
import {
  deleteProduct,
  downProduct,
  getCategoryList,
  getProductPage,
  upProduct
} from '../../api/admin'
import { formatPrice } from '../../utils/format'

const PRODUCT_QUERY_KEY = 'mall_admin_product_query'

export default {
  data() {
    return {
      query: {
        name: '',
        categoryId: '',
        pageNum: 1,
        pageSize: 20
      },
      categories: [],
      list: [],
      loading: false,
      loadError: false,
      total: 0
    }
  },
  created() {
    this.restoreQuery()
    this.loadCategories()
    this.loadData()
  },
  methods: {
    formatPrice,
    async loadCategories() {
      try {
        this.categories = await getCategoryList()
      } catch (error) {
        this.categories = []
        this.$message.error(error?.message || '商品分类加载失败')
      }
    },
    async loadData() {
      this.loading = true
      this.loadError = false
      try {
        const data = await getProductPage(this.query)
        this.list = data.list || []
        this.total = data.total || 0
      } catch (error) {
        this.loadError = true
      } finally {
        this.loading = false
      }
      this.persistQuery()
    },
    persistQuery() {
      sessionStorage.setItem(PRODUCT_QUERY_KEY, JSON.stringify(this.query))
    },
    restoreQuery() {
      const routeQuery = this.$route.query.q
      const saved = routeQuery || sessionStorage.getItem(PRODUCT_QUERY_KEY)
      if (!saved) {
        return
      }
      try {
        const parsed = JSON.parse(saved)
        this.query = {
          ...this.query,
          ...parsed
        }
      } catch (error) {
        sessionStorage.removeItem(PRODUCT_QUERY_KEY)
      }
    },
    categoryName(categoryId) {
      const current = this.categories.find(item => item.id === categoryId)
      return current ? current.name : `分类 ${categoryId}`
    },
    resetQuery() {
      this.query = {
        name: '',
        categoryId: '',
        pageNum: 1,
        pageSize: 20
      }
      this.loadData()
    },
    changePage(page) {
      this.query.pageNum = page
      this.loadData()
    },
    changePageSize(size) {
      this.query.pageSize = size
      this.query.pageNum = 1
      this.loadData()
    },
    goEdit(id) {
      this.$router.push({
        path: `/products/${id}`,
        query: {
          q: JSON.stringify(this.query)
        }
      })
    },
    goCreate() {
      this.$router.push({
        path: '/products/create',
        query: {
          q: JSON.stringify(this.query)
        }
      })
    },
    async toggleStatus(row) {
      const nextStatusText = row.status ? '下架' : '上架'
      try {
        if (row.status) {
          await downProduct(row.id)
        } else {
          await upProduct(row.id)
        }
        this.$message.success(`已${nextStatusText}`)
        this.loadData()
      } catch (error) {
        this.$message.error(error?.message || `商品${nextStatusText}失败`)
      }
    },
    removeRow(row) {
      this.$confirm(`确认删除“${row.name}”吗？`, '删除商品', {
        type: 'warning'
      }).then(async () => {
        try {
          await deleteProduct(row.id)
          this.$message.success('已删除')
          this.loadData()
        } catch (error) {
          this.$message.error(error?.message || '商品删除失败')
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.query-form {
  margin-bottom: 12px;
}

.state-block {
  margin-bottom: 16px;
}

.product-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-cell__image,
.product-cell__placeholder {
  width: 56px;
  height: 56px;
  border-radius: 10px;
}

.product-cell__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  color: #9ca3af;
  font-size: 12px;
}

.product-cell__content {
  min-width: 0;
}

.product-cell__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.product-cell__title {
  font-weight: 600;
}

.product-cell__meta {
  margin-top: 4px;
  color: #6b7280;
  font-size: 13px;
}

.product-cell__group-buy {
  margin-top: 6px;
  color: #b45309;
  font-size: 13px;
}

.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
}

.table-footer__meta {
  color: #6b7280;
  font-size: 13px;
}
</style>
